import React, { useEffect, useState, forwardRef } from "react";
import Webcam from "react-webcam";
import { useSelector } from "react-redux";
import Close from "../Assets/Images/Close.svg";
import Modal from "./Modal";
import { useNavigate } from "react-router-dom";
import Arrows from "../Assets/Images/arrowIndicator.gif";
import { carCount } from "../Redux/Features/carSlice";
import { updateClaimStatus } from '../utils/updateClaimStatus';

const Camera = forwardRef(
  ({ detection, startObjectDetection, result, cameraBlock, latency }, ref) => {
    const navigate = useNavigate();
    const car = useSelector((state) => state.car);

    const [retakeModal, setRetakeModal] = useState(false);
    const [detectionMsg, setDetectionMsg] = useState(detection);
    const [additionalLogging, setAdditionalLogging] = useState("");
    const [resultObj, setResultObj] = useState(result);
    const [cameraBlocked, setCameraBlocked] = useState(false);
    const [cameraPrompt, setCameraPrompt] = useState(false);

    const localMaxWidth = localStorage.getItem("maxWidth");
    const localMaxHeight = localStorage.getItem("maxHeight");

    const isClaim = useSelector((state) => state.isClaim);
    const isPartialImagesLive =
      Number(import.meta.env.VITE_IS_PARTIAL_IMAGES_LIVE || "0") === 1;

    const carCountStencil = isClaim === true && isPartialImagesLive ? 11 : 9;

    const handlePermissionChange = (event) => {
      const permissionStatus = event.target;
      const keyOfLC = "IsAlreadyRefreshed";

      if (permissionStatus.state === "granted") {
        if (!window.localStorage.getItem(keyOfLC)) {
          window.localStorage.setItem(keyOfLC, "1");
        }
        setCameraBlocked(false);
        setCameraPrompt(false);
      } else if (permissionStatus.state === "denied") {
        window.localStorage.removeItem(keyOfLC);
        setCameraBlocked(true);
        updateClaimStatus('CAMERA_BLOCK_POPUP');
        setCameraPrompt(false);
      }
    };

    useEffect(() => {
      let isMounted = true;

      navigator.permissions.query({ name: "camera" }).then((result) => {
        if (isMounted) {
          result.onchange = handlePermissionChange;
          handlePermissionChange({ target: result }); // Initial check
        }
      });

      return () => {
        isMounted = false;
      };
    }, []);

    useEffect(() => {
      if (import.meta.env.VITE_VERBOSE_LOGGING === "1") {
        const detectionString =
          "Detecting " +
          (result.final_side === undefined ? "nothing" : result.final_side) +
          " with " +
          "hP: " +
          (result.h_precision === undefined ? "-" : result.h_precision) +
          ", " +
          "vP: " +
          (result.v_precision === undefined ? "-" : result.v_precision) +
          ", " +
          "dP: " +
          (result.d_precision === undefined ? "-" : result.d_precision) +
          " at " +
          (result.coord === undefined
            ? "[0,0,0,0]"
            : JSON.stringify(result.coord)) +
          " w/ confidence " +
          (result.confidence === undefined ? "-" : result.confidence) +
          " | " +
          latency;

        setAdditionalLogging(detectionString);
      }
      setDetectionMsg(detection);
      setResultObj(result);
    }, [detection, result, latency]);

    const [videoConstraints, setVideoConstraints] = useState({
      facingMode: "environment",
      width: {
        ideal: Math.max(localMaxWidth || 0, 1024),
      },
      height: {
        ideal: Math.max(localMaxHeight || 0, 768),
      },
      frameRate: { ideal: 10, max: 10 },
    });


    const permissionBlocked = () => {
      setCameraBlocked(true);
      cameraBlock(true);
    };

    return (
      <>
        <div className="camera">
          <div className="focus">
            {car.retake && (
              <img
                src={Close}
                onClick={() => setRetakeModal(true)}
                alt="X"
                className="close"
              />
            )}
            <p
              className="top_name"
              style={{
                background: detectionMsg.value === "D" ? "#0F871B" : "#EC6466",
              }}
            >
              <span className="focus_text">{car.data[car.count - 1].name}</span>
            </p>
            <img
              className="stencil"
              src={
                car.count < carCountStencil
                  ? !resultObj.confidence ||
                    detectionMsg.value === "ND" ||
                    detectionMsg.value === "S"
                    ? car.data[car.count - 1].redStencil
                    : (resultObj.confidence > 10 &&
                      resultObj.confidence < 50) ||
                      detectionMsg.value === "M" // changed <70 ➜ <50
                      ? car.data[car.count - 1].stencil
                      : (resultObj.confidence >= 50 ||
                        detectionMsg.value === "D") &&
                      car.data[car.count - 1].greenStencil // changed >=70 ➜ >=50
                  : car.data[car.count - 1].stencil
              }
              alt=""
            />
            {car.data[car.count - 1].toValidate ? (
              <p
                className="bottom_name"
                style={{
                  background:
                    detectionMsg.value === "S"
                      ? "#EC6466"
                      : detectionMsg.value === "ND"
                        ? "#F20505"
                        : detectionMsg.value === "D"
                          ? "#0F871B"
                          : detectionMsg.value === "M" && "#000000",
                }}
              >
                <span className="focus_text">{detectionMsg.msg}</span>
                {import.meta.env.VITE_VERBOSE_LOGGING === '1' && (
                  <span className="focus_text">{additionalLogging}</span>
                )}
              </p>
            ) : (
              !isClaim && (
                <>
                  {car.data[car.count - 1].id === "odo-meter" && (
                    <p
                      className="bottom_name"
                      style={{
                        background: "#FFEB3B",
                        WebkitTextFillColor: "#000000",
                      }}
                    >
                      <span className="focus_text">
                        Turn ON the Engine before capturing Odo Meter
                      </span>
                    </p>
                  )}

                  {car.data[car.count - 1].id?.includes("sunroof") && (
                    <p
                      className="bottom_name"
                      style={{
                        background: "#FFEB3B",
                        WebkitTextFillColor: "#000000",
                      }}
                    >
                      <span className="focus_text">
                        Please ensure the Sunroof glass is clearly visible
                      </span>
                    </p>
                  )}
                </>
              )
            )}

          </div>

          {retakeModal && (
            <Modal>
              <div className="modalContainer modalRotate">
                <div className="retake_modal">
                  <span>
                    Are you sure you want to cancel the retake action?
                  </span>
                  <div className="yes_no_btn">
                    <button onClick={() => navigate("/upload")}>Yes</button>
                    <button onClick={() => setRetakeModal(false)}>No</button>
                  </div>
                </div>
              </div>
            </Modal>
          )}

          {cameraBlocked && (
            <Modal>
              <div className="modalContainer modalRotate">
                <div className="retake_modal">
                  <span>
                    Please allow camera access from your browser and refresh.
                  </span>
                  <div className="yes_no_btn">
                    <button
                      style={{ backgroundColor: "#e42125" }}
                      onClick={() => window.location.reload()}
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}
          {cameraPrompt && !cameraBlocked && (
            <Modal>
              <div className="promptModal">
                <div className="alertIcon">
                  <svg
                    fill="#f32b2b"
                    height="70px"
                    width="70px"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M14.2222 21H9.77778C6.65675 21 5.09624 21 3.97524 20.2646C3.48995 19.9462 3.07328 19.5371 2.74902 19.0607C2 17.9601 2 16.4279 2 13.3636C2 10.2994 2 8.76721 2.74902 7.6666C3.07328 7.19014 3.48995 6.78104 3.97524 6.46268C4.69555 5.99013 5.59733 5.82123 6.978 5.76086C7.63685 5.76086 8.20412 5.27068 8.33333 4.63636C8.52715 3.68489 9.37805 3 10.3663 3H13.6337C14.6219 3 15.4728 3.68489 15.6667 4.63636C15.7959 5.27068 16.3631 5.76086 17.022 5.76086C18.4027 5.82123 19.3044 5.99013 20.0248 6.46268C20.51 6.78104 20.9267 7.19014 21.251 7.6666C22 8.76721 22 10.2994 22 13.3636C22 16.4279 22 17.9601 21.251 19.0607C20.9267 19.5371 20.51 19.9462 20.0248 20.2646C18.9038 21 17.3433 21 14.2222 21ZM15.2697 9.59961C15.2697 9.1854 14.9339 8.84961 14.5197 8.84961C14.1055 8.84961 13.7697 9.1854 13.7697 9.59961V9.74316C12.2996 8.99864 10.4581 9.24066 9.22951 10.4692C7.69954 11.9992 7.69954 14.4798 9.22951 16.0098C10.7595 17.5397 13.2401 17.5397 14.77 16.0098C15.421 15.3588 15.7957 14.5337 15.8923 13.6844C15.9391 13.2728 15.6434 12.9012 15.2319 12.8544C14.8203 12.8076 14.4487 13.1033 14.4019 13.5148C14.3422 14.0392 14.112 14.5465 13.7094 14.9491C12.7652 15.8933 11.2344 15.8933 10.2902 14.9491C9.34598 14.0049 9.34598 12.4741 10.2902 11.5299C10.9379 10.8822 11.8617 10.6788 12.6828 10.9198C12.3737 11.0637 12.1928 11.4061 12.2661 11.7542C12.3515 12.1595 12.7492 12.4189 13.1546 12.3335L14.6743 12.0135C15.0213 11.9404 15.2697 11.6342 15.2697 11.2796V9.59961Z"
                      fill="#da2f2f"
                    />
                  </svg>
                </div>
                <div className="messageContainer">
                  <div className="retake_modal">
                    <span>Please allow camera access from your browser.</span>
                  </div>
                </div>
              </div>
            </Modal>
          )}
          <Webcam
            audio={false}
            ref={ref}
            forceScreenshotSourceSize
            className="video_camera"
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            onUserMedia={startObjectDetection}
            onUserMediaError={permissionBlocked}
          />
        </div>
      </>
    );
  }
);

export default Camera;
